import { useMemo } from "react";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";

/**
 * Custom hook to check if the current customer has a specific payment method
 * or is a quick pay only customer (allowedPaymentMethod === "kk")
 */
export const useCustomerPaymentMethod = () => {
    const { cvrAndCustomerNumbers, customerNumber } = useAppData();

    /**
     * Checks if the current customer is NOT a quick pay only customer
     * Returns true if customer can use normal checkout (allowedPaymentMethod !== "kk")
     * Returns false if customer should only use quick pay (cannot use normal checkout)
     */
    const isNotQuickPayOnlyCustomer = useMemo(() => {
        if (!cvrAndCustomerNumbers?.accounts || !customerNumber) return true;

        // Find the customer in the accounts array
        for (const account of cvrAndCustomerNumbers.accounts) {
            const customer = account.customers.find((c) => c.number === customerNumber);
            if (customer) {
                return customer.allowedPaymentMethod !== "kk";
            }
        }
        return true;
    }, [cvrAndCustomerNumbers, customerNumber]);

    /**
     * Gets the current customer's allowed payment method
     * Returns "kk", "p", or undefined if customer not found
     */
    const allowedPaymentMethod = useMemo(() => {
        if (!cvrAndCustomerNumbers?.accounts || !customerNumber) return undefined;

        // Find the customer in the accounts array
        for (const account of cvrAndCustomerNumbers.accounts) {
            const customer = account.customers.find((c) => c.number === customerNumber);
            if (customer) {
                return customer.allowedPaymentMethod;
            }
        }
        return undefined;
    }, [cvrAndCustomerNumbers, customerNumber]);

    return {
        isNotQuickPayOnlyCustomer,
        allowedPaymentMethod,
    };
};
